import { PageHero } from "@/components/PageHero";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, getSessionId } from "@/lib/api";
import { toast } from "sonner";
import {
    FolderOpen, ChevronDown, ArrowLeft, BookOpen, Scale, Download, ExternalLink,
    HeartHandshake, Compass, ArrowRight, Check, Stethoscope,
} from "lucide-react";
import CompletedDocsUpload from "@/components/CompletedDocsUpload";
import { useCountry } from "@/lib/country";
import { useAuth } from "@/lib/auth";

/* Documents — the family's care binder.
 *
 * What lives here:
 *   - Legal documents (MPOA / DPOA / Advance Directive / Living Will / POLST)
 *     explained in plain English, with mark-done state synced to the roadmap.
 *   - Uploads of completed/signed legal docs (CompletedDocsUpload).
 *   - A doorway to the unified Care Summary builder (/care-summary) — the
 *     platform's ONE share surface for medications, records, and documents.
 *     Legacy per-page shares (medication profile, Continuity Passport) were
 *     retired in Jun 2026.
 *
 * GUIDE-program screeners used to live here too; they moved to /guide so
 * the GUIDE flow (eligibility → screeners → share) is one calm ritual and
 * Documents stays focused on the documents themselves.
 */
export default function Documents() {
    const { country } = useCountry();
    const { user } = useAuth();
    // Admins always see the full toolset.
    const isUS = user?.role === "admin" || (country || "US").toUpperCase() === "US";
    return (
        <div className="max-w-5xl space-y-8 animate-fade-in-up">
            <PageHero
                kicker="Documents"
                kickerIcon={FolderOpen}
                title={<>Your family&apos;s care binder.</>}
                subtitle="Legal directives explained, your completed paperwork on file, and one Care Summary link to share medications and records with any clinician."
            />

            <LegalDocsExplainer />

            <CompletedDocsUpload />

            {/* Doorway to the unified Care Summary builder — the ONE place
                to mint a read-only share link (meds, records, documents). */}
            {isUS && (
                <Link
                    to="/care-summary"
                    data-testid="documents-passport-card"
                    className="ml-card ml-card-hover p-5 md:p-6 flex items-start gap-4 bg-teal-soft/40 !border-teal-light hover:!border-teal-dark transition-colors"
                >
                    <div className="shrink-0 w-12 h-12 rounded-2xl bg-white flex items-center justify-center">
                        <Compass strokeWidth={1.6} className="text-teal-dark" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="text-xs uppercase tracking-wider font-semibold text-teal-dark">Patient record</p>
                        <h2 className="mt-0.5 text-xl md:text-2xl font-heading font-medium text-[#12776F]">
                            Care Summary
                        </h2>
                        <p className="mt-1 text-sm text-[#4C5551] leading-relaxed">
                            One read-only link with your loved one&apos;s medications, uploaded documents,
                            timeline, and diagnoses — you pick exactly which sections to include.
                            Send it to any new clinician or care navigator.
                        </p>
                    </div>
                    <ArrowRight size={20} strokeWidth={1.6} className="text-teal-dark mt-2 shrink-0" />
                </Link>
            )}

            {/* Pointer to the GUIDE section — keeps Documents honest about
                where the screeners actually live. */}
            {isUS && (
                <Link
                    to="/guide"
                    data-testid="documents-guide-pointer"
                    className="ml-card ml-card-hover p-4 md:p-5 flex items-center gap-4 bg-cream-light hover:bg-cream-dark/40 transition-colors"
                >
                    <div className="shrink-0 w-10 h-10 rounded-2xl bg-teal-soft flex items-center justify-center">
                        <Stethoscope strokeWidth={1.6} className="text-teal-dark" size={18} />
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-[#1F2624]">Looking for the GUIDE eligibility check and pre-visit screeners?</p>
                        <p className="text-xs text-[#6E7A74] mt-0.5">They live in their own section now — eligibility on top, screeners below, one share link at the bottom.</p>
                    </div>
                    <ArrowRight size={18} strokeWidth={1.6} className="text-teal-dark shrink-0" />
                </Link>
            )}

            <Link to="/dashboard" className="text-sm text-[#6E7A74] inline-flex items-center gap-1.5 hover:text-[#4C5551]">
                <ArrowLeft size={14} /> Back to your dashboard
            </Link>
        </div>
    );
}


/* ---------------- LegalDocsExplainer ----------------
 * Caregiver-friendly comparison of the 5 most-confused planning documents:
 * MPOA, DPOA, Advance Directive, Living Will, POLST/MOLST.
 * Collapsed by default so the page stays calm; expands to a clear table +
 * plain-English guidance.
 */
const LEGAL_DOCS = [
    {
        id: "mpoa",
        name: "Medical Power of Attorney (MPOA)",
        question: "Who speaks for me?",
        when: "When you can't make medical decisions yourself.",
        who: "Patient",
        plain: "Names the person who makes healthcare choices for you when you can't speak for yourself. Sometimes called a Healthcare Proxy or HCPOA.",
        template_url: "https://www.caringinfo.org/planning/advance-directives/by-state/",
        template_label: "Free state-by-state template (CaringInfo)",
        tone: "teal",
        roadmap: { primary: { stage: "early", id: "e4" }, also: [{ stage: "middle", id: "m7" }] },
    },
    {
        id: "dpoa",
        name: "Durable Power of Attorney (DPOA)",
        question: "Who handles my affairs?",
        when: "When you can't manage finances or legal matters.",
        who: "Patient",
        plain: "Names the person who can pay bills, handle banking, sign documents, and make legal decisions on your behalf.",
        template_url: "https://www.lsc.gov/about-lsc/what-legal-aid/find-legal-aid",
        template_label: "Find free legal help in your state (LSC)",
        tone: "sage",
        roadmap: { primary: { stage: "early", id: "e5" }, also: [{ stage: "middle", id: "m7" }] },
    },
    {
        id: "advance_directive",
        name: "Advance Directive",
        question: "What do I want, and who speaks for me?",
        when: "Future healthcare planning — best done early.",
        who: "Patient",
        plain: "The umbrella document. Often combines a Living Will and an MPOA into one form. Different states call it different names.",
        template_url: "https://www.caringinfo.org/planning/advance-directives/by-state/",
        template_label: "Free state-by-state template (CaringInfo)",
        tone: "yellow",
        roadmap: { primary: { stage: "early", id: "e3" }, also: [{ stage: "middle", id: "m7" }] },
    },
    {
        id: "living_will",
        name: "Living Will",
        question: "What treatments do I want — or not want?",
        when: "End-of-life or serious illness situations.",
        who: "Patient",
        plain: "Spells out specific wishes — feeding tubes, CPR, breathing machines, comfort-only care — so your family doesn't have to guess.",
        template_url: "https://www.caringinfo.org/planning/advance-directives/by-state/",
        template_label: "Free state-by-state template (CaringInfo)",
        tone: "stage_middle",
        roadmap: { primary: { stage: "early", id: "e3" }, also: [{ stage: "middle", id: "m8" }] },
    },
    {
        id: "polst",
        name: "POLST / MOLST",
        question: "What are my medical orders right now?",
        when: "Serious illness, frailty, or advanced disease.",
        who: "Patient + clinician (signed together)",
        plain: "A medical order — not just a wish. EMS and hospitals follow it. Created with a doctor when illness is advanced. Stays at the bedside or on the fridge.",
        template_url: "https://polst.org/state-programs/",
        template_label: "Find your state's POLST form",
        tone: "stage_late",
        roadmap: { primary: { stage: "late", id: "l2" } },
    },
];

function isDoneForDoc(doc, progress) {
    const inStage = (s, id) => Array.isArray(progress?.[s]) && progress[s].includes(id);
    if (!doc.roadmap) return false;
    if (inStage(doc.roadmap.primary.stage, doc.roadmap.primary.id)) return true;
    return (doc.roadmap.also || []).some(p => inStage(p.stage, p.id));
}

const TONE_MAP = {
    teal:         { chip: "bg-teal-soft text-teal-dark border-teal-light",                       card: "bg-teal-soft/60 border-teal-light" },
    sage:         { chip: "bg-sage-light text-sage-dark border-sage-soft",                       card: "bg-sage-light/80 border-sage-soft" },
    yellow:       { chip: "bg-yellow-light text-yellow-dark border-yellow",                      card: "bg-yellow-light border-yellow" },
    stage_middle: { chip: "bg-stage_middle-light text-stage_middle-dark border-stage_middle",    card: "bg-stage_middle-light border-stage_middle" },
    stage_late:   { chip: "bg-stage_late-light text-stage_late-dark border-stage_late",          card: "bg-stage_late-light border-stage_late" },
};

function LegalDocsExplainer() {
    const sid = getSessionId();
    const [open, setOpen] = useState(false);
    const [progress, setProgress] = useState({ early: [], middle: [], late: [] });
    const [loadingProg, setLoadingProg] = useState(true);

    useEffect(() => {
        let active = true;
        (async () => {
            try {
                const r = await api.get(`/roadmap/${sid}`);
                if (!active) return;
                setProgress(r.data || { early: [], middle: [], late: [] });
            } catch { /* fail soft */ }
            finally { if (active) setLoadingProg(false); }
        })();
        return () => { active = false; };
    }, [sid]);

    const toggleRoadmap = async (doc, markDone) => {
        if (!doc.roadmap) return;
        const { stage, id } = doc.roadmap.primary;
        const current = progress[stage] || [];
        const next = markDone
            ? (current.includes(id) ? current : [...current, id])
            : current.filter(x => x !== id);
        const updated = { ...progress, [stage]: next };
        setProgress(updated); // optimistic
        try {
            await api.put(`/roadmap/${sid}`, updated);
            toast.success(markDone ? "Saved — synced to your roadmap." : "Reopened — roadmap updated.");
        } catch {
            setProgress(progress); // rollback
            toast.error("Couldn't sync with your roadmap.");
        }
    };

    const doneCount = LEGAL_DOCS.filter(d => isDoneForDoc(d, progress)).length;

    return (
        <section className="ml-card p-0 bg-gradient-to-br from-teal-soft/30 via-cream to-yellow-light/40 border border-teal-light overflow-hidden" data-testid="legal-docs-explainer">
            <button
                type="button"
                onClick={() => setOpen(o => !o)}
                data-testid="legal-docs-toggle"
                className="w-full flex items-center justify-between gap-3 px-5 md:px-6 py-4 hover:bg-cream-dark/40 transition-colors text-left"
            >
                <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-2xl bg-teal-soft flex items-center justify-center shrink-0">
                        <Scale size={18} strokeWidth={1.7} className="text-teal-dark" />
                    </div>
                    <div>
                        <h2 className="text-xl md:text-2xl font-heading font-medium text-[#12776F]">
                            MPOA, DPOA, Living Will, POLST — what&apos;s the difference?
                        </h2>
                        <p className="text-sm text-[#4C5551] mt-0.5">
                            Plain-English breakdown of the five planning documents most families mix up.
                            {!loadingProg && doneCount > 0 && (
                                <span className="ml-2 inline-flex items-center gap-1 text-sage-dark" data-testid="legal-docs-done-count">
                                    <Check size={12} /> {doneCount} of {LEGAL_DOCS.length} done
                                </span>
                            )}
                        </p>
                    </div>
                </div>
                <span className="inline-flex items-center gap-1 text-xs font-medium px-3 py-1.5 rounded-2xl bg-white border border-cream-dark text-teal-dark shrink-0">
                    {open ? "Hide" : "Learn the difference"}
                    <ChevronDown size={12} className={`transition-transform ${open ? "rotate-180" : ""}`} />
                </span>
            </button>

            {open && (
                <div className="px-5 md:px-6 pb-6">
                    <div className="rounded-2xl bg-white border border-teal-light p-4 mb-5 flex items-start gap-3 shadow-sm" data-testid="legal-docs-intro">
                        <BookOpen size={18} className="text-teal-dark mt-0.5 shrink-0" />
                        <div>
                            <p className="text-sm text-[#1F2624] leading-relaxed">
                                These documents answer different questions, and most families need <strong>more than one</strong>.
                                Two are about <em>who</em> decides for you (MPOA, DPOA). Two are about <em>what</em> you want
                                (Living Will, Advance Directive). The last one (POLST) is an actual medical order signed with a doctor.
                            </p>
                        </div>
                    </div>

                    <div className="space-y-3" data-testid="legal-docs-cards">
                        {LEGAL_DOCS.map(d => {
                            const done = isDoneForDoc(d, progress);
                            return (
                                <article
                                    key={d.id}
                                    data-testid={`legal-doc-${d.id}`}
                                    data-done={done ? "true" : "false"}
                                    className={`rounded-2xl p-4 md:p-5 border transition-colors ${done ? "bg-sage-light border-sage ring-2 ring-sage-soft/60" : (TONE_MAP[d.tone]?.card || "bg-white border-cream-dark")}`}
                                >
                                    <div className="flex flex-wrap items-baseline gap-3 mb-2">
                                        <h3 className="font-heading text-lg font-medium text-[#1F2624]">{d.name}</h3>
                                        <span className={`inline-flex items-center text-xs px-2.5 py-0.5 rounded-2xl border ${TONE_MAP[d.tone]?.chip || ""}`}>
                                            {d.who}
                                        </span>
                                        {done && (
                                            <span
                                                data-testid={`legal-doc-${d.id}-done-badge`}
                                                className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-0.5 rounded-2xl bg-sage text-white border border-sage-dark"
                                            >
                                                <Check size={11} strokeWidth={3} /> You&apos;ve completed this
                                            </span>
                                        )}
                                    </div>
                                    <p className="text-base text-teal-dark italic mb-2">&ldquo;{d.question}&rdquo;</p>
                                    <p className="text-sm text-[#4C5551] leading-relaxed">{d.plain}</p>
                                    {!done && (
                                        <p className="text-xs text-[#6E7A74] mt-2">
                                            <span className="uppercase tracking-wider font-semibold mr-1">When:</span>
                                            {d.when}
                                        </p>
                                    )}

                                    <div className="mt-3 flex flex-wrap gap-2">
                                        {done ? (
                                            <>
                                                <button
                                                    type="button"
                                                    onClick={() => toggleRoadmap(d, false)}
                                                    data-testid={`legal-doc-${d.id}-reopen`}
                                                    className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-2xl bg-white border border-sage-soft text-sage-dark hover:bg-sage-light/80"
                                                >
                                                    Mark needs updating
                                                </button>
                                                <Link
                                                    to="/roadmap"
                                                    data-testid={`legal-doc-${d.id}-roadmap-link`}
                                                    className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-2xl bg-transparent text-teal-dark hover:text-teal underline underline-offset-2"
                                                >
                                                    View on your roadmap →
                                                </Link>
                                            </>
                                        ) : (
                                            <>
                                                <button
                                                    type="button"
                                                    onClick={() => toggleRoadmap(d, true)}
                                                    data-testid={`legal-doc-${d.id}-mark-done`}
                                                    className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-2xl bg-sage text-white hover:bg-sage-dark"
                                                >
                                                    <Check size={12} /> I&apos;ve completed this
                                                </button>
                                                {d.template_url && (
                                                    <a
                                                        href={d.template_url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        data-testid={`legal-doc-${d.id}-template`}
                                                        className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-2xl bg-teal-soft border border-teal-light text-teal-dark hover:bg-teal-light/60"
                                                    >
                                                        <Download size={12} /> {d.template_label}
                                                        <ExternalLink size={11} className="opacity-70" />
                                                    </a>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </article>
                            );
                        })}
                    </div>

                    <div className="mt-5 overflow-x-auto rounded-2xl bg-white border border-cream-dark" data-testid="legal-docs-table">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-xs uppercase tracking-wider text-[#6E7A74] border-b border-cream-dark">
                                    <th className="py-2.5 px-3 font-semibold">Document</th>
                                    <th className="py-2.5 px-3 font-semibold">Answers</th>
                                    <th className="py-2.5 px-3 font-semibold">When used</th>
                                    <th className="py-2.5 px-3 font-semibold">Who completes it</th>
                                </tr>
                            </thead>
                            <tbody>
                                {LEGAL_DOCS.map(d => (
                                    <tr key={d.id} className="border-b border-cream-light last:border-b-0 align-top">
                                        <td className="py-2.5 px-3 text-[#1F2624] font-medium">{d.name}</td>
                                        <td className="py-2.5 px-3 text-teal-dark italic">&ldquo;{d.question}&rdquo;</td>
                                        <td className="py-2.5 px-3 text-[#4C5551]">{d.when}</td>
                                        <td className="py-2.5 px-3 text-[#4C5551]">{d.who}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="mt-5 rounded-2xl bg-gradient-to-br from-sage-light via-yellow-light/50 to-teal-soft/40 border border-sage-soft p-5" data-testid="legal-docs-no-capacity">
                        <div className="flex items-start gap-3">
                            <div className="w-10 h-10 rounded-2xl bg-sage text-white flex items-center justify-center shrink-0">
                                <HeartHandshake size={18} strokeWidth={1.7} />
                            </div>
                            <div className="flex-1 min-w-0">
                                <h3 className="font-heading text-lg font-medium text-[#1F2624]">
                                    What if my loved one can&apos;t complete these themselves?
                                </h3>
                                <p className="text-sm text-[#4C5551] mt-1 leading-relaxed">
                                    Cognition has to be intact enough to understand what they&apos;re signing. In dementia, that window closes — sometimes faster than families expect. Here&apos;s how to think about where you are:
                                </p>
                            </div>
                        </div>

                        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
                            <div className="rounded-2xl p-4 bg-sage-light/80 border border-sage-soft" data-testid="legal-no-capacity-early">
                                <p className="text-xs uppercase tracking-wider font-semibold text-sage-dark mb-1">If they can still discuss it</p>
                                <p className="text-sm text-[#1F2624]">
                                    <strong>Do it now</strong> — even a brief, witnessed conversation captured by an attorney counts. A good day in early dementia is enough to sign an MPOA / Advance Directive. Don&apos;t wait for a perfect moment.
                                </p>
                            </div>
                            <div className="rounded-2xl p-4 bg-yellow-light border border-yellow" data-testid="legal-no-capacity-borderline">
                                <p className="text-xs uppercase tracking-wider font-semibold text-yellow-dark mb-1">If capacity is fading</p>
                                <p className="text-sm text-[#1F2624]">
                                    An attorney can still meet with them and assess capacity at the table. Many will sign if your loved one can name themselves, their kids, and explain the basic idea. Ask the attorney to document the capacity finding.
                                </p>
                            </div>
                            <div className="rounded-2xl p-4 bg-teal-soft/60 border border-teal-light" data-testid="legal-no-capacity-late">
                                <p className="text-xs uppercase tracking-wider font-semibold text-teal-dark mb-1">If they can no longer sign</p>
                                <p className="text-sm text-[#1F2624]">
                                    New POAs aren&apos;t possible — but you have other paths:
                                </p>
                                <ul className="text-sm text-[#1F2624] mt-1.5 space-y-1 list-disc list-inside">
                                    <li><strong>Surrogate laws</strong> — most states recognise next-of-kin for medical decisions (spouse, adult child).</li>
                                    <li><strong>Guardianship / conservatorship</strong> — a court-appointed path. Slower, costs $1.5–6k, but available when no POA exists.</li>
                                    <li><strong>Representative payee</strong> — for Social Security only. Free, takes 4–6 weeks via SSA.</li>
                                </ul>
                            </div>
                        </div>

                        <p className="text-xs text-[#4C5551] mt-3">
                            <strong>Either way, talk to an elder-law attorney before doing nothing.</strong> Most offer a free 20-minute call.
                        </p>
                    </div>

                    <div className="mt-4 rounded-2xl bg-yellow-light border border-yellow p-4" data-testid="legal-docs-talk">
                        <p className="text-sm text-[#1F2624]">
                            <strong>Need help having this conversation?</strong> The{" "}
                            <Link to="/conversations#legal" className="text-teal-dark underline underline-offset-2 hover:text-teal">
                                Hard Talks → MPOA / DNR / POLST
                            </Link>{" "}
                            page has scripts for raising these documents with your loved one and your family.
                        </p>
                    </div>

                    <div className="mt-3 rounded-2xl bg-teal-soft/50 border border-teal-light p-4 text-sm text-[#1F2624]" data-testid="legal-docs-disclaimer">
                        <p className="font-semibold text-teal-dark mb-1">This is not legal or financial advice.</p>
                        <p className="text-[#4C5551]">
                            Forms, witness rules, and capacity thresholds vary by state. Memory Lane is educational support — for actual paperwork, please work with a licensed attorney. Find free or low-cost legal help in your state under{" "}
                            <Link to="/resources?category=legal_financial" className="text-teal-dark underline underline-offset-2 hover:text-teal font-medium">
                                Resources → Legal &amp; Financial
                            </Link>.
                        </p>
                    </div>
                </div>
            )}
        </section>
    );
}
